import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TuneIcon from '@mui/icons-material/Tune';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import cloud from 'd3-cloud';
import axios from '../../api/axiosInstance';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import { BarChart as MuiBarChart } from '@mui/x-charts/BarChart';
import { LineChart as MuiLineChart } from '@mui/x-charts/LineChart';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { LinePlot } from '@mui/x-charts/LineChart';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { RadarChart as MuiRadarChart } from '@mui/x-charts/RadarChart';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#00838f', '#d32f2f', '#7b1fa2', '#558b2f', '#0277bd', '#6d4c41'];
const getEasyRedmineTicketUrl = (ticketId) => `https://toolbox.easyredmine.com/issues/${ticketId}`;

function formatHours(value) {
  return `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}h`;
}

const BILLING_LABELS = {
  infogerance: 'Infogérance',
  commande: 'Sur devis',
  autre: 'Autre',
};

function matrixHours(value) {
  return Number(value || 0) ? formatHours(value) : '-';
}

function getRadarMax(values = []) {
  const maximum = Math.max(...values.map((value) => Number(value || 0)), 0);
  if (!maximum) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(maximum));
  const step = magnitude / 10;
  return Math.ceil(maximum / step) * step;
}

function useChartWidth() {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const updateWidth = () => setWidth(containerRef.current.clientWidth || 0);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return [containerRef, width];
}

function CompetenceRadar({ data, color, onClick, width, height = 300, margin }) {
  if (!data?.length) return <EmptyState label="Aucune compétence renseignée." />;
  const max = getRadarMax(data.map((item) => item.value));
  return (
    <MuiRadarChart
      width={width}
      height={height}
      radar={{
        metrics: data.map((item) => ({ name: item.subject, min: 0, max })),
        max,
      }}
      series={[{
        data: data.map((item) => Number(item.value || 0)),
        label: 'Heures passées',
        color,
        fillArea: true,
      }]}
      divisions={5}
      onAreaClick={onClick}
      onMarkClick={onClick}
      slotProps={{
        legend: {
          direction: 'column',
          position: { vertical: 'middle', horizontal: 'right' },
          padding: 0,
          labelStyle: { fontSize: 11 },
        },
      }}
      margin={margin || { top: 15, bottom: 15, left: 25, right: 25 }}
    />
  );
}

function ResponsiveCompetenceRadar({ data, color, onClick, height = 330, dialog = false }) {
  const [containerRef, containerWidth] = useChartWidth();
  const width = Math.max(dialog ? 1120 : 620, containerWidth || (dialog ? 1120 : 760));
  const chartMargin = dialog
    ? { top: 110, bottom: 130, left: 275, right: 275 }
    : { top: 58, bottom: 95, left: 105, right: 105 };

  return (
    <Box
      ref={containerRef}
      title={data?.map((item) => item.subject).join(' · ')}
      sx={{ width: '100%', minWidth: 0, overflow: dialog ? 'auto' : 'visible', display: 'flex', justifyContent: 'center' }}
    >
      <CompetenceRadar
        data={data}
        color={color}
        onClick={onClick}
        width={Math.min(width, dialog ? 1280 : 1180)}
        height={dialog ? Math.max(700, height) : height}
        margin={chartMargin}
      />
    </Box>
  );
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatOpeningDays(createdAt, closedAt) {
  if (!createdAt || !closedAt) return '-';
  const start = new Date(createdAt);
  const end = new Date(closedAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return '-';
  const days = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  return `${days.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} j`;
}

function formatDelta(value) {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${formatHours(number)}`;
}

function getContractReference(contract = {}) {
  return contract.reference || contract.ref || contract.contractNumber || contract.name || 'Contrat';
}

function getContractName(contract = {}) {
  const reference = getContractReference(contract);
  const name = contract.name || contract.title || '';
  return name && name !== reference ? name : '';
}

function getContractLabel(contract = {}) {
  const reference = getContractReference(contract);
  const name = getContractName(contract);
  return name ? `${reference} - ${name}` : reference;
}

function getContractAmount(contract = {}, annual = false) {
  return (contract.articles || []).reduce((sum, article) => {
    const total = Number(article.quantity || 0) * Number(article.unitPrice || 0);
    const cycle = article.serviceMetric === 'travel' ? 'annuel' : (article.billingCycle || 'oneshot');
    return sum + (annual ? (cycle === 'mensuel' ? total * 12 : total) : (cycle === 'annuel' ? total / 12 : total));
  }, 0);
}

function getContractPeriodLabel(contract, year) {
  if (!contract?.startDate || !year) return `Année ${year}`;
  const start = new Date(contract.startDate);
  if (Number.isNaN(start.getTime())) return `Année ${year}`;
  const from = new Date(year - 1, start.getMonth(), start.getDate());
  const to = new Date(year, start.getMonth(), start.getDate());
  to.setDate(to.getDate() - 1);
  return `${from.toLocaleDateString('fr-FR')} au ${to.toLocaleDateString('fr-FR')}`;
}

function ChartCard({ title, subtitle, children, minHeight = 320, sx = {} }) {
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%', minWidth: 0, overflow: 'hidden', ...sx }}>
      <Stack spacing={2.25} sx={{ height: '100%', minWidth: 0 }}>
        <Box>
          {typeof title === 'string' ? <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography> : title}
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <Box sx={{ height: minHeight, minHeight, width: '100%', minWidth: 0, overflow: 'visible', flex: '0 0 auto' }}>{children}</Box>
      </Stack>
    </Paper>
  );
}

function EmptyState({ label = 'Aucune donnée à afficher.' }) {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );
}



function ProfileTrackerChart({ data }) {
  if (!data?.length) return <EmptyState />;
  return <MuiBarChart
    height={230}
    xAxis={[{ scaleType: 'band', data: data.map((item) => item.profile) }]}
    yAxis={[{ width: 55, valueFormatter: (value) => formatHours(value) }]}
    series={[
      { data: data.map((item) => Number(item.infogerance || 0)), label: 'Infogérance', stack: 'tracker', color: '#0f766e' },
      { data: data.map((item) => Number(item.commande || 0)), label: 'Commande', stack: 'tracker', color: '#2563eb' },
      { data: data.map((item) => Number(item.autre || 0)), label: 'Autre', stack: 'tracker', color: '#94a3b8' },
    ]}
    slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'center' } } }}
    margin={{ top: 10, bottom: 45, left: 5, right: 10 }}
  />;
}

function MonthlyHoursChart({ rows, users, comparison, previousYearLabel, currentYearLabel, onMonthClick, isComparison }) {
  if (isComparison) {
    const data = comparison?.monthly || [];
    if (!data.length) return <EmptyState />;
    return <MuiBarChart
      height={350}
      xAxis={[{ scaleType: 'band', data: data.map((row) => row.month) }]}
      series={[
        { data: data.map((row) => Number(row.previous || 0)), label: previousYearLabel, color: '#94a3b8' },
        { data: data.map((row) => Number(row.current || 0)), label: currentYearLabel, color: '#0f766e' },
        { data: data.map((row) => Number(row.delta || 0)), label: 'Écart', color: '#ef4444' },
      ]}
      yAxis={[{ width: 55, valueFormatter: (value) => formatHours(value) }]}
      onItemClick={(_, item) => onMonthClick(data[item.dataIndex])}
      slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'center' } } }}
      margin={{ top: 10, bottom: 55, left: 5, right: 10 }}
    />;
  }
  if (!rows?.length) return <EmptyState />;
  return <Box sx={{ width: '100%' }}>
  <ChartsContainer
    height={370}
    xAxis={[{ scaleType: 'band', data: rows.map((row) => row.month), id: 'months' }]}
    yAxis={[{ id: 'hours', width: 55 }, { id: 'count', position: 'right', width: 55, min: 0 }]}
    series={[{ type: 'line', id: 'interventions', yAxisId: 'count', data: rows.map((row) => { const value = Number(row.interventions ?? row.interventionCount ?? row.intervention ?? 0); return value === 0 ? null : value; }), label: 'Interventions', color: '#ef4444' }, ...users.map((user, index) => ({ type: 'bar', id: `hours-${index}`, yAxisId: 'hours', data: rows.map((row) => { const value = Number(row[user] || 0); return value === 0 ? null : value; }), label: user, stack: 'time', color: COLORS[index % COLORS.length] }))]}
  >
    <BarPlot onItemClick={(_, item) => onMonthClick(rows[item.dataIndex])} />
    <LinePlot onMarkClick={(_, item) => onMonthClick(rows[item.dataIndex])} />
    <ChartsXAxis axisId="months" />
    <ChartsYAxis axisId="hours" />
    <ChartsYAxis axisId="count" />
    <ChartsTooltip trigger="axis" />
  </ChartsContainer>
  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mt: 1.5, px: 2 }}>
    {[{ label: 'Interventions', color: '#ef4444' }, ...users.map((user, index) => ({ label: user, color: COLORS[index % COLORS.length] }))].map((item) => (
      <Stack key={item.label} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: item.label === 'Interventions' ? 0 : 1, bgcolor: item.color }} />
        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
      </Stack>
    ))}
  </Stack>
  </Box>;
}

function InterventionsAreaChart({ rows, onMonthClick }) {
  if (!rows?.length) return <EmptyState />;
  const values = rows.map((row) => Number(row.interventions ?? row.interventionCount ?? row.intervention ?? 0));
  return <MuiLineChart
    height={300}
    xAxis={[{ scaleType: 'point', data: rows.map((row) => row.month) }]}
    yAxis={[{ width: 55, min: 0, valueFormatter: (value) => `${value}` }]}
    series={[{ data: values, label: 'Interventions', area: true, color: '#ef4444', showMark: true, curve: 'monotoneX', valueFormatter: (value) => `${value} interventions` }]}
    onMarkClick={(_, item) => onMonthClick(rows[item.dataIndex])}
    slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'center' } } }}
    margin={{ top: 10, bottom: 55, left: 5, right: 10 }}
  />;
}

function RegularizationCard({ title, data }) {
  const profiles = [
    { key: 'admin', label: 'Heures admin' },
    { key: 'engineer', label: 'Heures ingé' },
    { key: 'pilotage', label: 'Pilotage' },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        borderColor: data?.status === 'to_regularize' ? 'warning.main' : 'divider',
        bgcolor: data?.status === 'to_regularize' ? 'rgba(245, 158, 11, 0.06)' : 'background.paper',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{title}</Typography>
          <Chip
            size="small"
            color={data?.status === 'to_regularize' ? 'warning' : 'success'}
            label={data?.status === 'to_regularize' ? `${formatHours(data?.toRegularize?.total)} à régulariser` : 'OK'}
          />
        </Stack>
        {profiles.map((profile) => {
          const delta = Number(data?.delta?.[profile.key] || 0);
          return (
            <Stack key={profile.key} spacing={0.25}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{profile.label}</Typography>
                <Typography variant="body2" color={delta > 0 ? 'warning.dark' : 'success.dark'} sx={{ fontWeight: 800 }}>
                  {delta > 0 ? `${formatHours(delta)} à régul` : `${formatHours(Math.abs(delta))} disponible`}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {formatHours(data?.used?.[profile.key])} utilisé / {formatHours(data?.included?.[profile.key])} prévu ({formatDelta(delta)})
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

function RegularizationSection({ regularization }) {
  if (!regularization) return null;
  const rows = regularization.byContract || [];

  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Régularisations contrat</Typography>
          <Typography variant="body2" color="text.secondary">Comparaison entre les heures prévues dans les articles du contrat et les temps passés.</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item size={{xs: 12, md: 6}}>
            <RegularizationCard title="Mois en cours" data={regularization.month} />
          </Grid>
          <Grid item size={{xs: 12, md: 6}}>
            <RegularizationCard title="Période en cours" data={regularization.year} />
          </Grid>
        </Grid>
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Contrat</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Admin mois</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Ingé mois</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Admin année</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Ingé année</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{getContractReference(row)}</Typography>
                      {getContractName(row) ? (
                        <Typography variant="caption" color="text.secondary" noWrap>{getContractName(row)}</Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{formatDelta(row.month?.delta?.admin)}</TableCell>
                  <TableCell align="right">{formatDelta(row.month?.delta?.engineer)}</TableCell>
                  <TableCell align="right">{formatDelta(row.year?.delta?.admin)}</TableCell>
                  <TableCell align="right">{formatDelta(row.year?.delta?.engineer)}</TableCell>
                  <TableCell>{row.hasArticleHours ? `${row.articles?.length || 0} article(s)` : 'Champs contrat'}</TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>Aucun contrat actif à analyser.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
}

function PieBlock({ data }) {
  const [containerRef, containerWidth] = useChartWidth();
  if (!data?.length) return <EmptyState />;
  const width = Math.max(360, containerWidth || 520);
  const sortedData = [...data]
    .map((item, index) => ({ ...item, originalIndex: index }))
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  const total = sortedData.reduce((sum, item) => sum + Number(item.value || 0), 0);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: { xs: 390, sm: 292 }, minWidth: 0, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', gap: 2, overflow: 'visible' }}>
      <MuiPieChart
        width={Math.max(250, width - 175)}
        height={292}
        hideLegend
        margin={{ top: 12, bottom: 18, left: 8, right: 8 }}
        series={[{
          data: sortedData.map((item, index) => ({
            id: index,
            value: Number(item.value || 0),
            label: item.name,
            color: COLORS[index % COLORS.length],
          })),
          innerRadius: 52,
          outerRadius: 108,
          paddingAngle: 1,
          cornerRadius: 3,
        }]}
      />
      <Stack spacing={1.25} sx={{ minWidth: 145, maxWidth: 190 }}>
        {sortedData.map((item, index) => (
          <Stack key={`${item.name}-${index}`} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 14, height: 14, flex: '0 0 14px', borderRadius: '50%', bgcolor: COLORS[index % COLORS.length], boxShadow: '0 0 0 1px rgba(0,0,0,.14)' }} />
            <Typography variant="body2" sx={{ color: '#263238', fontWeight: 700, lineHeight: 1.2 }}>
              {item.name} <Box component="span" sx={{ color: '#087443', fontWeight: 900 }}>
                ({total ? Math.round((Number(item.value || 0) / total) * 100) : 0}%)
              </Box>
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}



const normalizeCloudTags = (words = [], limit = 150) => {
  const occurrences = new Map();

  words.filter((word) => word?.text).forEach((word) => {
    const value = String(word.text).trim();
    if (!value) return;
    const normalizedValue = value.toLocaleLowerCase('fr-FR');
    const existing = occurrences.get(normalizedValue);
    occurrences.set(normalizedValue, {
      value: existing?.value || value,
      count: (existing?.count || 0) + Math.max(1, Number(word.value || 1)),
    });
  });

  return Array.from(occurrences.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map((tag, index) => ({ ...tag, key: tag.value + '-' + index }));
};

/* legacy concentric positioning replaced by d3-cloud */
const buildConcentricPositions = (tags = [], seed = 1, compact = false) => {
  if (!tags.length) return [];

  const maxCount = Math.max(...tags.map((tag) => tag.count), 1);
  const rings = compact ? [1, 6, 10, 14, 18, 22] : [1, 8, 14, 20, 26, 32, 38, 11];
  const result = [];
  let cursor = 0;

  rings.forEach((capacity, ringIndex) => {
    const ringTags = tags.slice(cursor, cursor + capacity);
    cursor += capacity;
    if (!ringTags.length) return;

    ringTags.forEach((tag, slot) => {
      const isCenter = ringIndex === 0;
      const angle = isCenter
        ? 0
        : (-Math.PI / 2) + ((Math.PI * 2 * slot) / ringTags.length) + (seed * 0.37);
      const radiusX = isCenter ? 0 : (compact ? 8 : 11) + (ringIndex * (compact ? 6 : 8.5));
      const radiusY = isCenter ? 0 : (compact ? 7 : 9) + (ringIndex * (compact ? 5.7 : 7.8));
      const intensity = Math.sqrt(tag.count / maxCount);
      const size = isCenter
        ? (compact ? 38 : 48)
        : Math.max(
          compact ? 9 : 11,
          Math.min(
            compact ? 28 : 34,
            Math.round((compact ? 9 : 10) + (intensity * (compact ? 20 : 27)) - (ringIndex * 0.65)),
          ),
        );

      result.push({
        ...tag,
        size,
        left: 50 + (Math.cos(angle) * radiusX),
        top: 50 + (Math.sin(angle) * radiusY),
        color: COLORS[(result.length + seed) % COLORS.length],
        zIndex: isCenter ? 4 : Math.max(1, 4 - ringIndex),
      });
    });
  });

  return result;
};

function ConcentricCloud({ words = [], seed = 1, compact = false }) {
  const tags = useMemo(
    () => normalizeCloudTags(words, compact ? 55 : 78),
    [words, compact],
  );
  const containerRef = useRef(null);
  const [layoutWords, setLayoutWords] = useState([]);
  useEffect(() => {
    if (!containerRef.current || !tags.length) return undefined;
    const width = Math.max(320, Math.min(1080, containerRef.current.clientWidth || 800));
    const height = compact ? 200 : 420;
    const maxCount = Math.max(...tags.map((tag) => tag.count), 1);
    const layout = cloud().size([width, height]).words(tags.map((tag) => ({ ...tag, size: Math.max(compact ? 11 : 14, Math.min(compact ? 28 : 54, 12 + Math.sqrt(tag.count / maxCount) * (compact ? 18 : 42))) }))).padding(compact ? 3 : 5).rotate(() => 0).font('Arial').fontSize((word) => word.size).on('end', setLayoutWords);
    layout.start();
    return () => layout.stop();
  }, [tags, seed, compact]);
  if (!tags.length) return <EmptyState label="Aucun mot-clé pour cette sélection." />;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: compact ? '100%' : 1080,
        mx: 'auto',
        minHeight: compact ? { xs: 190, sm: 220 } : { xs: 330, sm: 360, md: 390 },
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        backgroundImage: 'radial-gradient(circle at center, rgba(166, 206, 131, 0.22), transparent 38%), radial-gradient(circle at bottom right, rgba(20, 124, 67, 0.10), transparent 42%)',
      }}
    >
      <Box ref={containerRef} sx={{ width: '100%', height: compact ? 220 : 460, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <svg width="100%" height={compact ? 200 : 420} viewBox={`0 0 ${Math.min(1080, containerRef.current?.clientWidth || 800)} ${compact ? 200 : 420}`} role="img" aria-label="Nuage de mots">
          <g transform={`translate(${Math.min(1080, containerRef.current?.clientWidth || 800) / 2}, ${(compact ? 200 : 420) / 2})`}>
            {layoutWords.map((word, index) => <text key={`${word.key}-${index}`} textAnchor="middle" transform={`translate(${word.x || 0},${word.y || 0})`} style={{ fontFamily: 'Arial, sans-serif', fontSize: `${word.size}px`, fontWeight: word.size > 35 ? 900 : 700, fill: COLORS[index % COLORS.length] }}>{word.value}</text>)}
          </g>
        </svg>
      </Box>
    </Box>
  );
}

function CloudTagPanel({ words = [], userClouds = [] }) {
  const [shuffleSeed, setShuffleSeed] = useState(1);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, overflow: 'hidden' }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>CloudTag</Typography>
            <Typography variant="body2" color="text.secondary">
              Mots fréquents des objets de tickets sur la période et les filtres sélectionnés.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={() => setShuffleSeed((seed) => seed + 1)}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, whiteSpace: 'nowrap', fontWeight: 700 }}
          >
            Mélanger
          </Button>
        </Stack>

        <ConcentricCloud words={words} seed={shuffleSeed} />

        {!!userClouds.length && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Par utilisateur
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              {userClouds.map((cloud) => (
                <Paper key={cloud.user} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ px: 0.5, mb: 0.5 }}>
                    <Typography sx={{ color: '#82a5d9', fontWeight: 800 }}>
                      {cloud.user}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatHours(cloud.total)}
                    </Typography>
                  </Stack>
                  <ConcentricCloud words={cloud.words || []} seed={shuffleSeed} compact />
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

export default function ClientTimeAnalytics({ embedded = false, cloudTagOnly = false, sharedFilters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('current');
  const [internalTrackerFilter, setInternalTrackerFilter] = useState('all');
  const [internalContractFilter, setInternalContractFilter] = useState('all');
  const [internalPeriodMode, setInternalPeriodMode] = useState('currentYear');
  const [internalSelectedYear, setInternalSelectedYear] = useState(new Date().getFullYear());
  const [internalCustomStartDate, setInternalCustomStartDate] = useState('');
  const [internalCustomEndDate, setInternalCustomEndDate] = useState('');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [selectedCompetence1, setSelectedCompetence1] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedMatrixDetails, setSelectedMatrixDetails] = useState(null);

  const trackerFilter = embedded ? sharedFilters.trackerFilter : internalTrackerFilter;
  const setTrackerFilter = embedded ? sharedFilters.setTrackerFilter : setInternalTrackerFilter;
  const contractFilter = embedded ? sharedFilters.contractId : internalContractFilter;
  const setContractFilter = embedded ? sharedFilters.setContractId : setInternalContractFilter;
  const periodMode = embedded ? sharedFilters.periodMode : internalPeriodMode;
  const setPeriodMode = embedded ? sharedFilters.setPeriodMode : setInternalPeriodMode;
  const selectedYear = embedded ? sharedFilters.selectedYear : internalSelectedYear;
  const setSelectedYear = embedded ? sharedFilters.setSelectedYear : setInternalSelectedYear;
  const customStartDate = embedded ? sharedFilters.customStartDate : internalCustomStartDate;
  const setCustomStartDate = embedded ? sharedFilters.setCustomStartDate : setInternalCustomStartDate;
  const customEndDate = embedded ? sharedFilters.customEndDate : internalCustomEndDate;
  const setCustomEndDate = embedded ? sharedFilters.setCustomEndDate : setInternalCustomEndDate;

  useEffect(() => {
    setDraftStartDate(customStartDate || '');
    setDraftEndDate(customEndDate || '');
  }, [customStartDate, customEndDate]);

  useEffect(() => {
    setSelectedCompetence1('');
  }, [trackerFilter, contractFilter, periodMode, selectedYear, customStartDate, customEndDate]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('periodMode', periodMode);
    if (trackerFilter !== 'all') params.set('trackerMode', trackerFilter);
    if (contractFilter !== 'all') params.set('contractId', contractFilter);
    if (periodMode !== 'month') params.set('year', String(selectedYear));
    if (periodMode === 'custom' && customStartDate && customEndDate) {
      params.set('startDate', customStartDate);
      params.set('endDate', customEndDate);
    }
    const query = params.toString();
    axios.get(`/api/client/dashboard${query ? `?${query}` : ''}`)
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setError('');
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || 'Impossible de charger les analyses client.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [trackerFilter, contractFilter, periodMode, selectedYear, customStartDate, customEndDate]);

  const analyticsRoot = data?.analytics || {};
  const contracts = data?.regularization?.byContract || data?.contracts?.active || data?.contracts?.items || [];
  const currentYearLabel = analyticsRoot.currentYearLabel || new Date().getFullYear();
  const previousYearLabel = analyticsRoot.previousYearLabel || currentYearLabel - 1;
  const analytics = viewMode === 'previous'
    ? analyticsRoot.previousYear || {}
    : analyticsRoot.currentYear || analyticsRoot || {};
  const comparison = analyticsRoot.comparison || {};
  const selectedContract = contracts.find((contract) => String(contract._id) === String(contractFilter));
  const apiPeriodStart = data?.period?.selectedYearStart ? new Date(data.period.selectedYearStart) : null;
  const apiPeriodEnd = data?.period?.selectedYearEnd ? new Date(data.period.selectedYearEnd) : null;
  const hasApiPeriod = apiPeriodStart && apiPeriodEnd
    && !Number.isNaN(apiPeriodStart.getTime()) && !Number.isNaN(apiPeriodEnd.getTime());
  const periodLabel = periodMode === 'custom'
    ? (customStartDate && customEndDate
      ? customStartDate.split('-').reverse().join('/') + ' au ' + customEndDate.split('-').reverse().join('/')
      : 'Sélectionnez les dates')
    : periodMode === 'month'
      ? (hasApiPeriod ? formatDate(apiPeriodStart) + ' au ' + formatDate(apiPeriodEnd) : 'Mois en cours')
      : selectedContract
        ? getContractPeriodLabel(selectedContract, selectedYear)
        : hasApiPeriod
          ? formatDate(apiPeriodStart) + ' au ' + formatDate(apiPeriodEnd)
          : 'Période ' + selectedYear;
  const chartPeriodLabel = periodLabel;
  const users = analytics.monthly?.users || [];
  const monthlyRows = useMemo(() => (analytics.monthly?.rows || []).map((row) => ({ ...row, ...users.reduce((acc, user) => ({ ...acc, [user]: row.users?.[user] || 0 }), {}) })), [analytics.monthly?.rows, users]);

  const totals = useMemo(() => {
    if (viewMode === 'compare') {
      return {
        hours: comparison.totals?.current || 0,
        previousHours: comparison.totals?.previous || 0,
        interventions: comparison.totals?.currentInterventions || 0,
        previousInterventions: comparison.totals?.previousInterventions || 0,
        deltaHours: Number(comparison.totals?.current || 0) - Number(comparison.totals?.previous || 0),
        projects: analytics.projectMatrix?.rows?.length || 0,
      };
    }

    return {
      hours: monthlyRows.reduce((sum, row) => sum + Number(row.total || 0), 0),
      interventions: monthlyRows.reduce((sum, row) => sum + Number(row.interventions || 0), 0),
      projects: analytics.projectMatrix?.rows?.length || 0,
    };
  }, [monthlyRows, analytics.projectMatrix?.rows, comparison.totals, viewMode]);

  const competenceGroups = useMemo(() => {
    const groups = new Map();
    (analytics.competenceMatrix?.rows || []).forEach((row) => {
      if (!groups.has(row.competence1)) {
        groups.set(row.competence1, {
          name: row.competence1,
          admin: 0,
          engineer: 0,
          pilotage: 0,
          total: 0,
          rows: [],
        });
      }
      const group = groups.get(row.competence1);
      group.admin += Number(row.admin || 0);
      group.engineer += Number(row.engineer || 0);
      group.pilotage += Number(row.pilotage || 0);
      group.total += Number(row.total || 0);
      group.rows.push(row);
    });
    return [...groups.values()].map((group) => ({
      ...group,
      admin: Number(group.admin.toFixed(2)),
      engineer: Number(group.engineer.toFixed(2)),
      pilotage: Number(group.pilotage.toFixed(2)),
      total: Number(group.total.toFixed(2)),
    }));
  }, [analytics.competenceMatrix?.rows]);
  const selectedCompetenceGroup = competenceGroups.find((group) => group.name === selectedCompetence1);
  const competence1RadarData = [...competenceGroups].sort((a, b) => b.total - a.total).map((group) => ({
    subject: group.name,
    value: Number(group.total || 0),
  }));
  const competenceColumnTotals = competenceGroups.reduce((totals, group) => ({
    admin: totals.admin + Number(group.admin || 0),
    engineer: totals.engineer + Number(group.engineer || 0),
    pilotage: totals.pilotage + Number(group.pilotage || 0),
    total: totals.total + Number(group.total || 0),
  }), { admin: 0, engineer: 0, pilotage: 0, total: 0 });
  const competence2RadarData = useMemo(() => {
    const rows = selectedCompetenceGroup?.rows || analytics.competenceMatrix?.rows || [];
    const details = new Map();
    rows.forEach((row) => {
      const subject = row.competence2 || 'Non renseigné';
      details.set(subject, Number(details.get(subject) || 0) + Number(row.total || 0));
    });
    return [...details.entries()]
      .map(([subject, value]) => ({ subject, value: Number(value.toFixed(2)) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  }, [analytics.competenceMatrix?.rows, selectedCompetenceGroup]);
  const handleRadarClick = (_, item) => {
    if (!Number.isInteger(item?.dataIndex)) return;
    const selected = competence1RadarData[item.dataIndex];
    if (selected?.subject) setSelectedCompetence1(selected.subject);
  };
  const selectedMonthDetails = selectedMonth?.details || [];
  const selectedMonthTotal = selectedMonthDetails.reduce((sum, entry) => sum + Number(entry.hours || 0), 0);
  const activeDetailsDialog = selectedMatrixDetails || (selectedMonth ? {
    title: `Temps passés - ${selectedMonth.month} ${selectedMonth.year}`,
    details: selectedMonthDetails,
    total: selectedMonthTotal,
  } : null);
  const groupedTimeDetails = useMemo(() => {
    const groups = new Map();
    (activeDetailsDialog?.details || []).forEach((entry) => {
      const key = entry.ticketId || entry.ticketSubject || 'Sans ticket';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(entry);
    });
    return [...groups.entries()].map(([ticket, entries]) => ({ ticket, entries, total: entries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0) }));
  }, [activeDetailsDialog]);

  const openMatrixDetails = ({ project, billingMode = 'all', user = '', month = '', label }) => {
    const details = (analytics.monthly?.rows || [])
      .flatMap((row) => (row.details || []).map((entry) => ({ ...entry, matrixMonth: row.month })))
      .filter((entry) => (
        entry.projectName === project
        && (billingMode === 'all' || entry.trackerMode === billingMode)
        && (!user || entry.user === user)
        && (!month || entry.matrixMonth === month)
      ))
      .sort((a, b) => new Date(b.spentOn || 0) - new Date(a.spentOn || 0));

    setSelectedMatrixDetails({
      title: `Temps passés - ${label || project}`,
      details,
      total: details.reduce((sum, entry) => sum + Number(entry.hours || 0), 0),
    });
  };

  const openCompetenceDetails = ({ competence1, competence2, profile = '', label }) => {
    const details = (analytics.monthly?.rows || [])
      .flatMap((row) => row.details || [])
      .filter((entry) => (
        entry.competence === competence1
        && (competence2 === undefined || entry.competence2 === competence2)
        && (!profile || entry.profile === profile)
      ))
      .sort((a, b) => new Date(b.spentOn || 0) - new Date(a.spentOn || 0));

    setSelectedMatrixDetails({
      title: `Temps passés - ${label || competence1}`,
      details,
      total: details.reduce((sum, entry) => sum + Number(entry.hours || 0), 0),
    });
  };

  const MatrixDetailsCell = ({ value, onOpen, emphasized = false, monthIndex = -1, total = false, tone = 'user' }) => {
    const clickable = Number(value || 0) > 0;
    const rowBackground = tone === 'project'
      ? 'rgba(167,243,208,.12)'
      : tone === 'billing'
        ? 'rgba(167,243,208,.12)'
        : monthIndex >= 0
          ? (monthIndex % 2 ? 'rgba(167,243,208,.08)' : 'rgba(167,243,208,.14)')
          : 'background.paper';
    return (
      <TableCell
        align="right"
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? onOpen : undefined}
        onKeyDown={clickable ? (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen();
          }
        } : undefined}
        sx={{
          minWidth: total ? 92 : 72,
          px: 0.75,
          py: 0.65,
          fontWeight: emphasized ? 800 : 500,
          cursor: clickable ? 'pointer' : 'default',
          transition: 'background-color 120ms ease',
          bgcolor: total ? 'success.dark' : rowBackground,
          color: total ? '#fff !important' : 'inherit',
          position: total ? 'sticky' : 'static',
          right: total ? 0 : 'auto',
          zIndex: total ? 1 : 'auto',
          borderLeft: total ? '2px solid' : undefined,
          borderColor: total ? 'success.main' : undefined,
          '&:hover': clickable ? { bgcolor: total ? 'success.main' : 'action.hover', textDecoration: 'underline' } : undefined,
          '&:focus-visible': clickable ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } : undefined,
        }}
      >
        {matrixHours(value)}
      </TableCell>
    );
  };

  const openMonthDetails = (rowOrEvent) => {
    const row = rowOrEvent?.payload || rowOrEvent?.activePayload?.[0]?.payload || rowOrEvent;
    if (!row?.month) return;
    const sourceRows = viewMode === 'compare' ? analyticsRoot.currentYear?.monthly?.rows || [] : analytics.monthly?.rows || [];
    const rowIndex = sourceRows.findIndex((item) => item.month === row.month);
    const detailRow = rowIndex >= 0 ? sourceRows[rowIndex] : row;
    const rowDate = detailRow.date || detailRow.monthDate || detailRow.periodStart;
    const inferredYear = rowDate && !Number.isNaN(new Date(rowDate).getTime())
      ? new Date(rowDate).getFullYear()
      : (detailRow.year || detailRow.yearLabel || (apiPeriodStart && rowIndex >= 0 ? apiPeriodStart.getFullYear() + Math.floor((apiPeriodStart.getMonth() + rowIndex) / 12) : (viewMode === 'previous' ? previousYearLabel : selectedYear)));
    setSelectedMonth({ ...detailRow, year: inferredYear });
  };

  if (loading) {
    return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (cloudTagOnly) {
    return <CloudTagPanel words={analytics.wordCloud || []} userClouds={analytics.wordCloudByUser || []} />;
  }

  return (
    <Stack spacing={4}>
      {!embedded && (
        <>
          <Stack direction="column" alignItems="stretch" spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ color: 'success.dark', fontWeight: 800 }}>{data?.organization?.name || 'Client'}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>Analyse des temps</Typography>
              <Typography color="text.secondary">Vue annuelle des temps passés, interventions, profils et compétences.</Typography>
            </Box>
          </Stack>
          {!selectedContract && (
            <Alert severity="info" sx={{ py: 0.75 }}>Sélectionnez un contrat pour afficher l’analyse des temps.</Alert>
          )}
          <Stack spacing={1.25} sx={{ width: '100%' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems="stretch">
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id="client-time-contract-label">Contrat</InputLabel>
              <Select
                labelId="client-time-contract-label"
                value={contractFilter}
                label="Contrat"
                onChange={(event) => { setContractFilter(event.target.value); setSelectedMonth(null); }}
                renderValue={() => selectedContract ? (
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 0.75 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={900} noWrap>{getContractReference(selectedContract)}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{getContractName(selectedContract) || 'Contrat de service'}</Typography>
                    </Box>
                    <Chip size="small" color="success" variant="outlined" label={`${getContractAmount(selectedContract).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} / mois`} />
                    <Chip size="small" variant="outlined" label={`${getContractAmount(selectedContract, true).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} / an`} />
                  </Stack>
                ) : 'Sélectionner un contrat'}
                sx={{ minHeight: 112, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 2 } }}
              >
                <MenuItem value="all">Sélectionner un contrat</MenuItem>
                {contracts.map((contract) => (
                  <MenuItem key={contract._id} value={contract._id}>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{getContractReference(contract)}</Typography>
                      {getContractName(contract) ? <Typography variant="caption" color="text.secondary">{getContractName(contract)}</Typography> : null}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedContract && (
              <Stack direction="column" spacing={1} alignItems="stretch" justifyContent="center">
                <Button variant="contained" color="success" startIcon={<VisibilityIcon />} onClick={() => { window.location.href = '/app/contracts'; }}>
                  Voir le contrat
                </Button>
                <Button variant="contained" color="info" startIcon={<DownloadIcon />} disabled>
                  Télécharger
                </Button>
              </Stack>
            )}
          </Stack>
          <Paper variant="outlined" sx={{ p: 0.5, borderColor: 'divider', bgcolor: 'transparent' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={0.75} sx={{ minWidth: 115, px: 0.75, alignContent:'center' }}>
                <CalendarMonthIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Période</Typography>
              </Stack>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={periodMode}
                onChange={(_, value) => value && setPeriodMode(value)}
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="month">Mois en courss</ToggleButton>
                <ToggleButton value="currentYear">Période en cours</ToggleButton>
                <ToggleButton value="year">Par période</ToggleButton>
                <ToggleButton value="custom">Personnalisée</ToggleButton>
              </ToggleButtonGroup>
              {periodMode === 'year' && (
                <FormControl size="small" sx={{ minWidth: 250 }}>
                  <InputLabel id="client-time-year-label">Période contractuelle</InputLabel>
                  <Select
                    labelId="client-time-year-label"
                    value={selectedYear}
                    label="Période contractuelle"
                    onChange={(event) => setSelectedYear(Number(event.target.value))}
                  >
                    {Array.from({ length: 8 }, (_, index) => new Date().getFullYear() - 5 + index).map((year) => (
                      <MenuItem key={year} value={year}>{year} - {getContractPeriodLabel(selectedContract, year)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {periodMode === 'custom' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <DatePicker
                    label="Date de début"
                    value={draftStartDate ? dayjs(draftStartDate) : null}
                    maxDate={draftEndDate ? dayjs(draftEndDate) : undefined}
                    onChange={(value) => setDraftStartDate(value?.isValid() ? value.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: { size: 'small', fullWidth: true, sx: { backgroundColor: '#fff !important', borderRadius: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#fff !important' }, '& .MuiInputBase-root': { backgroundColor: '#fff !important' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiInputLabel-root': { color: '#374151' }, '& .MuiInputBase-input': { color: '#111827' }, '& .MuiSvgIcon-root': { color: '#374151' } },cssUnderline: {
				'&:after': {
					borderBottomColor: 'white',
				},
			},
			cssLabel: {
				'&$cssFocused': {
					color: 'white',
				},
			}, },
                      field: { clearable: true },
                    }}
                    sx={{ width: { xs: '100%', sm: 240 }, minWidth: { sm: 240 } }}
                  />
                  <DatePicker
                    label="Date de fin"
                    value={draftEndDate ? dayjs(draftEndDate) : null}
                    minDate={draftStartDate ? dayjs(draftStartDate) : undefined}
                    onChange={(value) => setDraftEndDate(value?.isValid() ? value.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: { size: 'small', fullWidth: true, sx: { backgroundColor: '#fff !important', borderRadius: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#fff !important' }, '& .MuiInputBase-root': { backgroundColor: '#fff !important' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fff !important' }, '& .MuiInputLabel-root': { color: '#374151' }, '& .MuiInputBase-input': { color: '#111827' }, '& .MuiSvgIcon-root': { color: '#374151' } } },
                      field: { clearable: true },
                    }}
                    sx={{ width: { xs: '100%', sm: 240 }, minWidth: { sm: 240 } }}
                  />
                  <Button
                    variant="contained"
                    size="medium"
                    disabled={!draftStartDate || !draftEndDate || draftStartDate > draftEndDate}
                    onClick={() => { setCustomStartDate(draftStartDate); setCustomEndDate(draftEndDate); }}
                    sx={{ minWidth: 112, minHeight: 40, fontWeight: 800 }}
                  >
                    Appliquer
                  </Button>
                </Stack>
              )}
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', flex: '0 0 1px', alignSelf: 'stretch', bgcolor: 'divider' }} />
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 120, px: 0.75 }}>
                <TuneIcon fontSize="small" color="success" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Temps passé</Typography>
              </Stack>
              <ToggleButtonGroup size="small" exclusive value={trackerFilter} onChange={(_, value) => { if (value) { setTrackerFilter(value); setSelectedMonth(null); } }}>
                <ToggleButton value="all">Tous</ToggleButton>
                <ToggleButton value="infogerance"><SupportAgentIcon sx={{ mr: 0.5, fontSize: 16 }} />Infogérance</ToggleButton>
                <ToggleButton value="commande"><ShoppingCartIcon sx={{ mr: 0.5, fontSize: 16 }} />Sur devis</ToggleButton>
                <ToggleButton value="autre"><MoreHorizIcon sx={{ mr: 0.5, fontSize: 16 }} />Autres</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            </Paper>
          </Stack>
        </>
      )}

      <Box sx={{ display: selectedContract ? 'contents' : 'none' }}>
      {!embedded && (
        <Typography variant="h5" sx={{ mt: 2.5, mb: 1.5, fontWeight: 900 }}>
          Du {periodLabel}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Heures consommées</Typography>
            <Typography variant="h3" sx={{ mt: 0.5, fontWeight: 900 }}>{formatHours(totals.hours)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {trackerFilter === 'all' ? 'Tous les trackers' : trackerFilter === 'infogerance' ? 'Infogérance' : trackerFilter === 'commande' ? 'Sur devis' : 'Autres'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Interventions</Typography>
            <Typography variant="h3" sx={{ mt: 0.5, fontWeight: 900 }}>{totals.interventions}</Typography>
            <Typography variant="body2" color="text.secondary">
              {trackerFilter === 'all' ? 'Tous les trackers' : trackerFilter === 'infogerance' ? 'Infogérance' : trackerFilter === 'commande' ? 'Sur devis' : 'Autres'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <ChartCard
        title={`Niveau par type de facturation - ${chartPeriodLabel}`}
        subtitle="Infogérance et commandes classées selon le champ Facturable."
        minHeight={235}
        sx={{ width: { xs: '100%', lg: '58%' }, maxWidth: '100%', my: 2 }}
      >
        <ProfileTrackerChart data={analytics.byProfileTracker || []} />
      </ChartCard>

      <ChartCard
        title={viewMode === 'compare' ? `Comparatif ${currentYearLabel} / ${previousYearLabel}` : `Temps passé et interventions - ${chartPeriodLabel}`}
        subtitle={viewMode === 'compare' ? 'Comparaison mensuelle des heures et écart entre les deux années.' : 'Heures consommées empilées par intervenant.'}
        minHeight={390}
        sx={{ my: 2 }}
      >
        {viewMode === 'compare' ? (
          <MonthlyHoursChart rows={monthlyRows} users={users} comparison={comparison} previousYearLabel={previousYearLabel} currentYearLabel={currentYearLabel} onMonthClick={openMonthDetails} isComparison />
        ) : monthlyRows.length ? (
          <MonthlyHoursChart rows={monthlyRows} users={users} onMonthClick={openMonthDetails} />
        ) : <EmptyState />}
      </ChartCard>


      <Grid container spacing={2} sx={{ mt: 1.5, mb: 1.5 }}>
        <Grid item size={{xs: 12, md: 4}}>
          <ChartCard title="Par profil" minHeight={315}><PieBlock data={analytics.byProfile || []} /></ChartCard>
        </Grid>
        <Grid item size={{xs: 12, md: 4}}>
          <ChartCard title="Par activité" minHeight={315}><PieBlock data={analytics.byActivity || []} /></ChartCard>
        </Grid>
        <Grid item size={{xs: 12, md: 4}}>
          <ChartCard title="Par facturation" minHeight={315}><PieBlock data={analytics.byBilling || []} /></ChartCard>
        </Grid>
      </Grid>

      <Box sx={{ my: 2 }}>
        <ChartCard
          title="Temps par compétence"
          subtitle="Cliquez sur une compétence 1 pour filtrer le radar des compétences 2."
          minHeight={700}
        >
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ height: 650, borderRadius: 2, p: 1.5, overflow: 'visible' }}>
                <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, fontWeight: 800 }}>Compétences 1</Typography>
                <ResponsiveCompetenceRadar data={competence1RadarData} color="#147c43" height={600} onClick={handleRadarClick} />
              </Paper>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ height: 650, borderRadius: 2, p: 1.5, overflow: 'visible' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, pt: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Compétences 2</Typography>
                {selectedCompetence1 && <Chip size="small" color="success" label={`Filtré : ${selectedCompetence1}`} onDelete={() => setSelectedCompetence1('')} />}
                </Stack>
                <ResponsiveCompetenceRadar data={competence2RadarData} color="#0f766e" height={600} onClick={() => {}} />
              </Paper>
            </Grid>
          </Grid>
        </ChartCard>
      </Box>

      </Box>

      <Dialog
        fullScreen
        open={!!activeDetailsDialog}
        onClose={() => { setSelectedMonth(null); setSelectedMatrixDetails(null); }}
      >
        <Stack spacing={2} sx={{ p: 2.5, height: '100%', bgcolor: 'background.default' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>{activeDetailsDialog?.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatHours(activeDetailsDialog?.total)} au total · {activeDetailsDialog?.details?.length || 0} ligne(s)
              </Typography>
            </Box>
            <IconButton onClick={() => { setSelectedMonth(null); setSelectedMatrixDetails(null); }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TableContainer sx={{ flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1050 }}><TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
              {['Ticket', 'Intervenant', 'Tracker', 'Activité', 'Niveau', 'Heures', 'Déplacement', 'Commentaire'].map((label) => <TableCell key={label} sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{label}</TableCell>)}
            </TableRow></TableHead><TableBody>
              {groupedTimeDetails.map((group) => <Fragment key={group.ticket}>
                <TableRow hover sx={{ bgcolor: 'action.hover' }}><TableCell><Chip size="small" color="primary" variant="outlined" label={group.ticket === 'Sans ticket' ? group.ticket : `#${group.ticket}`} /> <Typography component="span" variant="body2" sx={{ ml: 1, fontWeight: 800 }}>{group.entries[0]?.ticketSubject || 'Temps passé'}</Typography></TableCell><TableCell>{group.entries[0]?.user || '-'}</TableCell><TableCell>{group.entries[0]?.tracker || '-'}</TableCell><TableCell>{group.entries[0]?.activity || '-'}</TableCell><TableCell>-</TableCell><TableCell><Chip size="small" color="success" label={`${formatHours(group.total)} · ${group.entries.length}`} /></TableCell><TableCell>-</TableCell><TableCell>{group.entries[0]?.comments || '-'}</TableCell></TableRow>
                {group.entries.map((entry, index) => <TableRow key={entry._id || `${group.ticket}-${index}`}><TableCell sx={{ pl: 5, color: 'text.secondary' }}>{formatDate(entry.spentOn)}</TableCell><TableCell>{entry.user || '-'}</TableCell><TableCell>{entry.tracker || '-'}</TableCell><TableCell>{entry.activity || '-'}</TableCell><TableCell><Chip size="small" variant="outlined" label={entry.competence || '-'} /></TableCell><TableCell><Chip size="small" color="success" label={formatHours(entry.hours)} /></TableCell><TableCell>{Number(entry.travel || entry.displacement || 0) > 0 ? <Chip size="small" color="warning" label="Oui" /> : '-'}</TableCell><TableCell>{entry.comments || '-'}</TableCell></TableRow>)}
              </Fragment>)}
            </TableBody></Table>
          </TableContainer>
          <TableContainer sx={{ display: 'none', flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, width: 92 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 280 }}>Ticket</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 145 }}>Tâche créée</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 145 }}>Tâche clôturée</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, width: 86 }}>Ouverture</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 90 }}>Tracker</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 120 }}>Activité</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 280 }}>Commentaire</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 210 }}>Compétence 1</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 300 }}>Compétence 2</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 150 }}>Utilisateur</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, width: 70 }}>Heures</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(activeDetailsDialog?.details || []).map((entry) => (
                  <TableRow key={entry._id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.spentOn)}</TableCell>
                    <TableCell sx={{ minWidth: 220, maxWidth: 260 }}>
                      {entry.ticketId ? (
                        <Link
                          href={getEasyRedmineTicketUrl(entry.ticketId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          variant="body2"
                          sx={{ fontWeight: 800 }}
                        >
                          #{entry.ticketId}
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>#-</Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.25,
                          fontSize: '0.68rem',
                        }}
                        title={entry.ticketSubject || ''}
                      >
                        {entry.ticketSubject || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.ticketCreatedAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.ticketClosedAt)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{formatOpeningDays(entry.ticketCreatedAt, entry.ticketClosedAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.tracker || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.activity || '-'}</TableCell>
                    <TableCell sx={{ minWidth: 280, maxWidth: 360 }}>
                      <Typography
                        variant="caption"
                        color={entry.comments ? 'text.primary' : 'text.secondary'}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.25,
                          fontSize: '0.7rem',
                        }}
                        title={entry.comments || ''}
                      >
                        {entry.comments || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 210, maxWidth: 260 }}>
                      {entry.competence ? (
                        <Chip
                          size="small"
                          label={entry.competence}
                          variant="outlined"
                          color="primary"
                          sx={{
                            maxWidth: 230,
                            height: 18,
                            fontSize: '0.64rem',
                            '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 300, maxWidth: 420 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.18,
                          fontSize: '0.7rem',
                        }}
                        title={entry.competence2 || ''}
                      >
                        {entry.competence2 || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>{entry.user || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{formatHours(entry.hours)}</TableCell>
                  </TableRow>
                ))}
                {!(activeDetailsDialog?.details || []).length && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ color: 'text.secondary' }}>Aucun temps passé pour cette sélection.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Matrice projets / mois</Typography>
            <Typography variant="body2" color="text.secondary">{`Synthèse des heures par projet, facturation et intervenant, sur ${chartPeriodLabel}.`}</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table
              size="small"
              stickyHeader
              sx={{ color: 'text.primary', '& .MuiTableCell-root': { borderColor: 'divider', color: 'text.primary' }, '& .MuiTableCell-head': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#20382d' : '#e7f3eb', backgroundImage: 'none' } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900, minWidth: 290, backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#20382d' : '#e7f3eb', color: 'text.primary' }}>Projet / facturation / utilisateur</TableCell>
                  {(analytics.projectMatrix?.months || []).map((month, index) => (
                    <TableCell
                      key={month}
                      align="right"
                      sx={{
                        minWidth: 72,
                        px: 0.75,
                        fontWeight: 900,
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                          ? (index % 2 ? '#294236' : '#355044')
                          : (index % 2 ? '#edf7f0' : '#e7f3eb'),
                        color: 'text.primary',
                      }}
                    >
                      {month}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      minWidth: 92,
                      px: 0.75,
                      fontWeight: 900,
                      bgcolor: 'success.dark',
                      color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#111827',
                      position: 'sticky',
                      right: 0,
                      zIndex: 3,
                        borderLeft: '2px solid', borderColor: 'success.main',
                        backgroundImage: 'none',
                      }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(analytics.projectMatrix?.rows || []).map((row) => {
                  const months = analytics.projectMatrix?.months || [];
                  const billingRows = Array.isArray(row.billing) && row.billing.length
                    ? row.billing
                    : [{
                      mode: 'all',
                      total: row.total,
                      months: row.months,
                      users: Object.entries(row.users || {}).map(([user, total]) => ({ user, total, months: {} })),
                    }];

                  return [
                    <TableRow key={`${row.project}-total`} sx={{ '& td': { fontWeight: 800 } }}>
                      <TableCell sx={{ pl: 1.5, py: 0.7, fontWeight: 900, bgcolor: 'rgba(167,243,208,.18)', color: 'text.primary', borderLeft: '4px solid', borderColor: 'success.main' }}>{row.project}</TableCell>
                      {months.map((month, monthIndex) => (
                        <MatrixDetailsCell
                          key={month}
                          value={row.months?.[month]}
                          emphasized
                          monthIndex={monthIndex}
                          tone="project"
                          onOpen={() => openMatrixDetails({ project: row.project, month, label: `${row.project} · ${month}` })}
                        />
                      ))}
                      <MatrixDetailsCell
                        value={row.total}
                        emphasized
                        total
                        tone="project"
                        onOpen={() => openMatrixDetails({ project: row.project, label: row.project })}
                      />
                    </TableRow>,
                    ...billingRows.flatMap((billingRow) => [
                      <TableRow key={`${row.project}-${billingRow.mode}`}>
                        <TableCell sx={{ pl: 3, py: 0.65, fontWeight: 800, bgcolor: 'rgba(167,243,208,.08)', color: 'text.primary' }}>
                          {BILLING_LABELS[billingRow.mode] || billingRow.mode || 'Facturation'}
                        </TableCell>
                        {months.map((month, monthIndex) => (
                          <MatrixDetailsCell
                            key={month}
                            value={billingRow.months?.[month]}
                            emphasized
                            monthIndex={monthIndex}
                            tone="billing"
                            onOpen={() => openMatrixDetails({
                              project: row.project,
                              billingMode: billingRow.mode,
                              month,
                              label: `${row.project} · ${BILLING_LABELS[billingRow.mode] || billingRow.mode} · ${month}`,
                            })}
                          />
                        ))}
                        <MatrixDetailsCell
                          value={billingRow.total}
                          emphasized
                          total
                          tone="billing"
                          onOpen={() => openMatrixDetails({
                            project: row.project,
                            billingMode: billingRow.mode,
                            label: `${row.project} · ${BILLING_LABELS[billingRow.mode] || billingRow.mode}`,
                          })}
                        />
                      </TableRow>,
                      ...(billingRow.users || []).map((userRow) => (
                        <TableRow key={`${row.project}-${billingRow.mode}-${userRow.user}`} hover>
                          <TableCell sx={{ pl: 5, py: 0.55 }}>{userRow.user}</TableCell>
                          {months.map((month, monthIndex) => (
                            <MatrixDetailsCell
                              key={month}
                              value={userRow.months?.[month]}
                              monthIndex={monthIndex}
                              onOpen={() => openMatrixDetails({
                                project: row.project,
                                billingMode: billingRow.mode,
                                user: userRow.user,
                                month,
                                label: `${row.project} · ${BILLING_LABELS[billingRow.mode] || billingRow.mode} · ${userRow.user} · ${month}`,
                              })}
                            />
                          ))}
                          <MatrixDetailsCell
                            value={userRow.total}
                            emphasized
                            total
                            onOpen={() => openMatrixDetails({
                              project: row.project,
                              billingMode: billingRow.mode,
                              user: userRow.user,
                              label: `${row.project} · ${BILLING_LABELS[billingRow.mode] || billingRow.mode} · ${userRow.user}`,
                            })}
                          />
                        </TableRow>
                      )),
                    ]),
                  ];
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Matrice compétences</Typography>
            <Typography variant="body2" color="text.secondary">{`Temps passé par compétence 1, compétence 2 et niveau, sur ${chartPeriodLabel}.`}</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table
              size="small"
              stickyHeader
              sx={{ color: 'text.primary', '& .MuiTableCell-root': { borderColor: 'divider', color: 'text.primary' }, '& .MuiTableCell-head': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#20382d' : '#e7f3eb', backgroundImage: 'none' } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900, minWidth: 250, backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#20382d' : '#e7f3eb', color: 'text.primary' }}>Compétence 1</TableCell>
                  <TableCell sx={{ fontWeight: 900, minWidth: 360, backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#294236' : '#edf7f0', color: 'text.primary' }}>Compétence 2</TableCell>
                  {[
                    ['Admin', competenceColumnTotals.admin, 'rgba(167,243,208,.14)'],
                    ['Ingé', competenceColumnTotals.engineer, 'rgba(167,243,208,.1)'],
                    ['Pilotage', competenceColumnTotals.pilotage, 'rgba(167,243,208,.14)'],
                  ].map(([label, total, bgcolor]) => (
                    <TableCell key={label} align="right" sx={{ minWidth: 96, px: 0.75, fontWeight: 900, bgcolor, color: 'text.primary' }}>
                      <Stack spacing={0.15} alignItems="flex-end"><span>{label}</span><Typography variant="caption" sx={{ fontWeight: 900 }}>{formatHours(total)}</Typography></Stack>
                    </TableCell>
                  ))}
                    <TableCell align="right" sx={{ minWidth: 100, px: 0.75, fontWeight: 900, bgcolor: 'success.dark', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#111827', position: 'sticky', right: 0, zIndex: 3, borderLeft: '2px solid', borderColor: 'success.main' }}>
                    <Stack spacing={0.15} alignItems="flex-end"><span>Total</span><Typography variant="caption" sx={{ color: 'inherit', fontWeight: 900 }}>{formatHours(competenceColumnTotals.total)}</Typography></Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {competenceGroups.map((group) => ([
                  <TableRow key={`${group.name}-total`}>
                    <TableCell sx={{ pl: 1.5, py: 0.7, fontWeight: 900, bgcolor: 'rgba(167,243,208,.18)', color: 'text.primary', borderLeft: '4px solid', borderColor: 'success.main' }}>{group.name}</TableCell>
                    <TableCell sx={{ py: 0.7, bgcolor: 'rgba(167,243,208,.1)', color: 'text.primary', fontWeight: 700 }}>Sous-total</TableCell>
                    <MatrixDetailsCell value={group.admin} emphasized tone="project" onOpen={() => openCompetenceDetails({ competence1: group.name, profile: 'Admin', label: `${group.name} · Admin` })} />
                    <MatrixDetailsCell value={group.engineer} emphasized tone="project" onOpen={() => openCompetenceDetails({ competence1: group.name, profile: 'Ingé', label: `${group.name} · Ingé` })} />
                    <MatrixDetailsCell value={group.pilotage} emphasized tone="project" onOpen={() => openCompetenceDetails({ competence1: group.name, profile: 'Pilotage', label: `${group.name} · Pilotage` })} />
                    <MatrixDetailsCell value={group.total} emphasized total tone="project" onOpen={() => openCompetenceDetails({ competence1: group.name, label: group.name })} />
                  </TableRow>,
                  ...group.rows.map((row) => (
                    <TableRow key={`${group.name}-${row.competence2 || 'sans-detail'}`} hover>
                      <TableCell sx={{ pl: 3, py: 0.55, color: 'text.secondary' }} aria-label={group.name} />
                      <TableCell sx={{ py: 0.55 }}>{row.competence2 || '-'}</TableCell>
                      <MatrixDetailsCell value={row.admin} onOpen={() => openCompetenceDetails({ competence1: group.name, competence2: row.competence2, profile: 'Admin', label: `${group.name} · ${row.competence2 || 'Non renseigné'} · Admin` })} />
                      <MatrixDetailsCell value={row.engineer} onOpen={() => openCompetenceDetails({ competence1: group.name, competence2: row.competence2, profile: 'Ingé', label: `${group.name} · ${row.competence2 || 'Non renseigné'} · Ingé` })} />
                      <MatrixDetailsCell value={row.pilotage} onOpen={() => openCompetenceDetails({ competence1: group.name, competence2: row.competence2, profile: 'Pilotage', label: `${group.name} · ${row.competence2 || 'Non renseigné'} · Pilotage` })} />
                      <MatrixDetailsCell value={row.total} emphasized total onOpen={() => openCompetenceDetails({ competence1: group.name, competence2: row.competence2, label: `${group.name} · ${row.competence2 || 'Non renseigné'}` })} />
                    </TableRow>
                  )),
                ]))}
                {!competenceGroups.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>Aucune compétence renseignée.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </Stack>
  );
}
